/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { ROLES } from '@config/constants/roles';
import { TAcl } from '@global-types/app/app';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { Navigate } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

interface ProtectedRouteProps {
	children: React.ReactNode;
	roles?: ROLES[];
	acls?: TAcl[];
}

/* -------------------------------------------------------------------------- */
/*                              Protected Router                              */
/* -------------------------------------------------------------------------- */
/**
 * ProtectedRoute Component
 * Handles route protection based on user roles and policies
 * Redirects unauthorized users to appropriate routes
 */
export const ProtectedRoute: React.FC<
	ProtectedRouteProps
> = ({ children, roles, acls }: ProtectedRouteProps) => {
	const { getUser } = useStore();
	const { checkAcl, checkRole, checkAcls } =
		useAuthorization();
	const user = getUser();

	// Handle unauthenticated users
	if (!user) {
		localStorage.removeItem('user');
		return <Navigate to="/login" />;
	}

	// Check role-based authorization
	if (roles && !checkRole(roles)) {
		return <Navigate to="/" />;
	}

	// Check acl-based authorization
	if (acls && !checkAcls(acls)) {
		return <Navigate to="/" />;
	}

	return children;
};

export default ProtectedRoute;
